import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {EMPTY, finalize, never, of, switchMap} from 'rxjs';
import {catchError, concatMap, delay, filter, map, mergeMap, tap} from 'rxjs/operators';
import * as GlobalMessageActions from '../../core/actions/global-message.actions';
import * as LayoutActions from '../../core/actions/layout.actions';
import {GlobalMessage, GlobalMessageType} from '../../core/models/global-message';
import * as QuoteActions from '../actions/quote.action';
import {QuotesError} from '../model/quotes-error';
import {QuoteService} from '../services/quote.service';

@Injectable()
export class QuoteEffects {

    closeQuoteList = createEffect(() => this.actions$
        .pipe(
            ofType(LayoutActions.closeQuoteList),
            // without the delay the quote list closing does not look smooth enough
            delay(500),
            map(a => QuoteActions.select({command: {quote: null}}))
        ));

    create$ = createEffect(() => this.actions$
        .pipe(
            ofType(QuoteActions.createQuote),
            tap(action => console.log('Quotes effect create', action)),
            mergeMap((action) => {
                    return this.service.createQuote(action.command)
                        .pipe(
                            map(quote => QuoteActions.createQuoteSuccess({command: action.command, quote: quote})),
                            catchError(error => of(QuoteActions.createQuoteFailure({
                                command: action.command,
                                error: (<QuotesError>error).errorMessage
                            })))
                        );
                }
            )
        ));

    creationFailure$ = createEffect(() => this.actions$
        .pipe(
            ofType(QuoteActions.createQuoteFailure),
            tap(action => console.log('Quote effect creation failure', action)),
            mergeMap((action) => {
                    const message: GlobalMessage = {
                        id: `quote-create-${action.command.name}-error`,
                        type: GlobalMessageType.ERROR,
                        message: `The quote '${action.command.name}' couldn't be created: ${action.error}`
                    };
                    return of(GlobalMessageActions.addMessage({globalMessage: message}));
                }
            )
        ));

    delete$ = createEffect(() => this.actions$
        .pipe(
            ofType(QuoteActions.deleteQuote),
            tap(quote => console.log('Quotes effect delete', quote)),
            mergeMap((action) => {
                return this.service.deleteQuote(action.command.quote)
                    .pipe(
                        map(() => QuoteActions.deleteQuoteSuccess({command: action.command})),
                        catchError(error => of(QuoteActions.deleteQuoteFailure({
                            command: action.command,
                            error: (<QuotesError>error).errorMessage
                        })))
                    );
            })
        ));

    deletionFailure$ = createEffect(() => this.actions$
        .pipe(
            ofType(QuoteActions.deleteQuoteFailure),
            tap(action => console.log('Quote effect deletion failure', action)),
            mergeMap((action) => {
                    const message: GlobalMessage = {
                        id: `quote-delete-${action.command.quote.id}-error`,
                        type: GlobalMessageType.ERROR,
                        message: `The quote '${action.command.quote.name}' couldn't be deleted: ${action.error}`
                    };
                    return of(GlobalMessageActions.addMessage({globalMessage: message}));
                }
            )
        ));

    edit$ = createEffect(() => this.actions$
        .pipe(
            ofType(QuoteActions.editQuote),
            tap(action => console.log('Quotes effect edit', action)),
            concatMap((action) => {
                return this.service.editQuote(action.command)
                    .pipe(
                        map(quoteResult => QuoteActions.editQuoteSuccess({command: action.command, quote: quoteResult})),
                        catchError(error => of(QuoteActions.editQuoteFailure({
                            command: action.command,
                            error: (<QuotesError>error).errorMessage
                        })))
                    );
            })
        ));

    editFailure$ = createEffect(() => this.actions$
        .pipe(
            ofType(QuoteActions.editQuoteFailure),
            tap(action => console.log('Quote effect edit failure', action)),
            mergeMap((action) => {
                    const message: GlobalMessage = {
                        id: `quote-edit-${action.command.quote.id}-error`,
                        type: GlobalMessageType.ERROR,
                        message: `The quote '${action.command.quote.name}' couldn't be edited: ${action.error}`
                    };
                    return of(GlobalMessageActions.addMessage({globalMessage: message}));
                }
            )
        ));

    request$ = createEffect(() => this.actions$
        .pipe(
            ofType(QuoteActions.request),
            tap(action => console.log('Quote effect request', action)),
            concatMap((action) => {
                return this.service.request(action.command)
                    .pipe(
                        map(quoteResult => QuoteActions.requestSuccess({command: action.command, quote: quoteResult})),
                        catchError(error => of(QuoteActions.requestFailure({
                            command: action.command,
                            error: (<QuotesError>error).errorMessage
                        })))
                    );
            })
        ));

    download$ = createEffect(() => this.actions$
        .pipe(
            ofType(QuoteActions.download),
            tap(action => console.log('Quote effect request', action)),
            concatMap((action) => {
                return this.service.downloadQuote(action.quote)
                    .pipe(
                        map(quoteResult => QuoteActions.downloadSuccess({blob: quoteResult})),
                        catchError(error => of(QuoteActions.downloadFailure({
                            command: action.quote,
                            error: (<QuotesError>error).errorMessage
                        })))
                    );
            })
        ));

    downloadSuccess$ = createEffect(() => this.actions$
        .pipe(
            ofType(QuoteActions.downloadSuccess),
            tap(action => {
                const blob = new Blob([action.blob], {type: 'application/pdf'});
                const blobUrl = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = `hering-berlin-quote.pdf`;
                link.click();
            }),
            filter(() => false)
        ));

    downloadFailure$ = createEffect(() => this.actions$
        .pipe(
            ofType(QuoteActions.downloadFailure),
            tap(action => console.log('Quote effect request failure', action)),
            mergeMap((action) => {
                    const message: GlobalMessage = {
                        id: `quote-download-${action.command.id}-error`,
                        type: GlobalMessageType.ERROR,
                        message: `The quote '${action.command.name}' couldn't be downloaded: ${action.error}`
                    };
                    return of(GlobalMessageActions.addMessage({globalMessage: message}));
                }
            )
        ));

    requestFailure$ = createEffect(() => this.actions$
        .pipe(
            ofType(QuoteActions.requestFailure),
            tap(action => console.log('Quote effect request failure', action)),
            mergeMap((action) => {
                    const message: GlobalMessage = {
                        id: `quote-request-${action.command.quote.id}-error`,
                        type: GlobalMessageType.ERROR,
                        message: `The quote '${action.command.quote.name}' couldn't be requested: ${action.error}`
                    };
                    return of(GlobalMessageActions.addMessage({globalMessage: message}));
                }
            )
        ));


    constructor(private actions$: Actions, private service: QuoteService) {
    }
}
